import React from "react";

import { css, cx } from "@emotion/css";
import { Controller, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { FormInputError } from "@/uiKit/atoms/forms/FormInputError.tsx";
import type { HTMLAutocomplete } from "@/uiKit/atoms/inputs/AutoComplete.ts";
import { DateInput, DateInputProps } from "@/uiKit/atoms/inputs/DateInput";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { retrieveFormError } from "@/utils/formUtils.ts";

export type BaseFormDateInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  DateInputProps,
  "value"
> & {
  name: TName;
  labelType?: "default" | "left";
  reserveErrorSpace?: boolean;
  autoComplete?: HTMLAutocomplete;
};

export type FormDateInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = AtomProps<
  BaseFormDateInputProps<TFieldValues, TName>,
  StyleClasses<"root" | "content" | "label" | "error">
>;

export const FormDateInput = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  id,
  name,
  label,
  labelType = "default",
  reserveErrorSpace,
  onChange,
  onBlur,
  disabled,
  className,
  classes,
  ...props
}: FormDateInputProps<TFieldValues, TName>): React.ReactElement => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <Controller
      name={name}
      disabled={disabled}
      render={({ field, fieldState }) => {
        const error = retrieveFormError(fieldState.error);
        return (
          <div className={cx(styles.container, className)}>
            <div className={cx(styles.content, classes?.content)}>
              {!!label && labelType === "left" && (
                <Typography
                  variant="body"
                  className={cx({ [styles.label]: true, [styles.labelError]: error.hasError }, classes?.label)}
                >
                  {label}
                </Typography>
              )}
              <DateInput
                id={id}
                className={cx(styles.root, className)}
                {...props}
                {...field}
                value={field.value ?? undefined}
                onChange={(value, event) => {
                  onChange?.(value, event);
                  field.onChange({
                    target: {
                      value,
                    },
                  });
                }}
                onBlur={event => {
                  onBlur?.(event);
                  field.onBlur();
                }}
                label={labelType === "default" ? label : undefined}
                error={error.hasError}
              />
            </div>
            <FormInputError
              reserveSpace={reserveErrorSpace}
              errorMessage={error.errorMessage ? t(error.errorMessage) : undefined}
              className={cx(styles.error, classes?.error)}
            />
          </div>
        );
      }}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    minWidth: 0,
  }),
  content: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: theme.spacing(6),
    overflow: "clip",
  }),
  root: css({}),
  label: css({}),
  labelError: css({
    color: theme.palette.error.main,
  }),
  error: css({}),
});
